/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { Contact, SaveContact } from 'model/contact';
import {
    CONTACT_ERROR,
    CONTACT_CLEAR_ERROR,
    CONTACT_SAVE_START,
    CONTACT_SAVE_SUCCESS,
    CONTACT_FETCH_START,
    CONTACT_FETCH_SUCCESS,
    CONTACT_DELETE_START,
    CONTACT_DELETE_SUCCESS,
    ContactAction
} from './ContactActionsTypes';
import { convertToContact, convertToSaveContactDto } from './ContactConverter';
import { ContactDto, FetchContactsDto } from './ContactDtos';
import { showErrorThunk } from '@spike/notifications-action';

const contactsUrl = '/organization_contacts';

// Extract meaningful error message from API error response
const extractErrorMessage = (apiError: any): string => {
    // Handle Axios error structure
    if (apiError?.response?.data) {
        const { data } = apiError.response;

        // Check for validation errors (422 status) - direct field errors like {"contact_type":["has already been taken"]}
        if (typeof data === 'object' && !data.message && !data.error && !data.errors) {
            // Look for field validation errors directly on data object
            const fieldNames = Object.keys(data);
            if (fieldNames.length > 0) {
                const firstField = fieldNames[0];
                const fieldError = data[firstField];
                if (Array.isArray(fieldError) && fieldError.length > 0) {
                    return `${firstField} ${fieldError[0]}`;
                }
                if (typeof fieldError === 'string') {
                    return `${firstField} ${fieldError}`;
                }
            }
        }

        // Check for validation errors in 'errors' property
        if (data.errors) {
            if (typeof data.errors === 'object') {
                const errorKeys = Object.keys(data.errors);
                if (errorKeys.length > 0) {
                    const firstKey = errorKeys[0];
                    const firstError = data.errors[firstKey];
                    if (Array.isArray(firstError) && firstError.length > 0) {
                        return `${firstKey} ${firstError[0]}`;
                    }
                    return `${firstKey} ${firstError}`;
                }
            }
            if (typeof data.errors === 'string') {
                return data.errors;
            }
        }

        // Check for standard error message
        if (data.message) {
            return data.message;
        }

        // Check for standard error field
        if (data.error) {
            return data.error;
        }
    }

    // Fallback to generic error
    return apiError?.message || 'An unexpected error occurred';
};

const error = (err: string): ContactAction => {
    return {
        type: CONTACT_ERROR,
        payload: {
            error: err
        }
    };
};

export const clearContactError = (): ContactAction => {
    return {
        type: CONTACT_CLEAR_ERROR
    };
};

/**
 * Fetch Contacts - GET
 * Retrieves all contacts for a given organization
 * @param organizationId - The organization ID to fetch contacts for
 */
export const fetchContactsThunk = (
    organizationId: number
): ThunkAction<Promise<Contact[]>, RootState, null, ContactAction> => {
    return async dispatch => {
        dispatch({ type: CONTACT_FETCH_START });

        try {
            const { data }: AxiosResponse<FetchContactsDto> = await ApiClient.get(contactsUrl, {
                params: { organization_id: organizationId }
            });

            // Convert the DTO response to Contact model array
            const contacts = Array.isArray(data) ? data.map(convertToContact) : [];

            dispatch({
                type: CONTACT_FETCH_SUCCESS,
                payload: {
                    contacts
                }
            });

            return contacts;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

/**
 * Save Contact - POST/PATCH
 * Creates a new contact (POST) or updates an existing contact (PATCH)
 * @param contact - The contact data to save
 * @param contactId - The ID of the contact to update (optional, if not provided creates new contact)
 */
export const saveContactThunk = (
    contact: SaveContact,
    contactId?: number
): ThunkAction<Promise<Contact>, RootState, null, ContactAction> => {
    return async dispatch => {
        dispatch({ type: CONTACT_SAVE_START });

        const url = contactId ? `${contactsUrl}/${contactId}` : contactsUrl;
        const apiMethod = contactId ? ApiClient.patch : ApiClient.post;

        try {
            const payload = convertToSaveContactDto(contact);
            const { data }: AxiosResponse<ContactDto> = await apiMethod(url, payload, {
                params: { organization_id: contact.organizationId }
            });

            // Convert the DTO response to Contact model
            const savedContact = convertToContact(data);

            dispatch({
                type: CONTACT_SAVE_SUCCESS,
                payload: {
                    contact: savedContact
                }
            });

            return savedContact;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};

/**
 * Delete Contact - DELETE
 * Hard deletes an existing contact
 * @param contactId - The ID of the contact to delete
 * @param organizationId - The organization ID (required for query param)
 */
export const deleteContactThunk = (
    contactId: number,
    organizationId: number
): ThunkAction<Promise<void>, RootState, null, ContactAction> => {
    return async dispatch => {
        dispatch({ type: CONTACT_DELETE_START });

        try {
            const url = `${contactsUrl}/${contactId}`;
            await ApiClient.delete(url, {
                params: { organization_id: organizationId }
            });

            dispatch({
                type: CONTACT_DELETE_SUCCESS,
                payload: {
                    contactId
                }
            });
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};
